import { supabase } from './supabaseStorage';
import { FormSection } from '../types/formBuilder';

interface ModelConfig {
  modelName: string;
  description: string;
  category: string;
  subcategory?: string;
  tags: string;
  workflowPath: string;
  outputKey: string;
  outputType: string;
  iconName: string;
  iconColor: string;
  iconBg: string;
  creditCost: number;
  estimatedTime: string;
  route: string;
  sortOrder: number;
  isAvailable: boolean;
  comingSoon: boolean;
  featured: boolean;
  fal_stream_node_id: string;
  fal_polling_node_name: string;
  is_long_running: boolean;
  polling_interval_seconds: number;
  /** 'stream' | 'queue' — which FAL client API to use */
  fal_client_mode: 'stream' | 'queue';
  /** Per-model override for fal.queue.submit webhookUrl; empty uses project default */
  fal_queue_webhook_url: string;
}

export interface SaveModelConfigRequest {
  modelConfig: ModelConfig;
  sections: FormSection[];
  isUpdate?: boolean;
  existingModelId?: string;
  currentUserId?: string;
}

interface SaveModelConfigResult {
  success: boolean;
  modelId?: string;
  uuid?: string;
  error?: string;
}

/**
 * Save or update model configuration in the modelconfig table
 */
export const saveModelConfiguration = async ({
  modelConfig,
  sections,
  isUpdate = false,
  existingModelId,
  currentUserId
}: SaveModelConfigRequest): Promise<SaveModelConfigResult> => {
  try {
    console.log('💾 [MODEL-SAVE] Starting save operation');
    console.log('📋 [MODEL-SAVE] Is update:', isUpdate);
    console.log('🆔 [MODEL-SAVE] Existing ID:', existingModelId);

    // Generate model_id from model name if creating new
    const modelId = existingModelId || modelConfig.modelName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'unnamed-model';

    console.log('🏷️ [MODEL-SAVE] Model ID:', modelId);

    // Convert form sections to input_schema format
    const inputSchema = sections.flatMap(section => 
      section.fields.map(field => ({
        key: field.variable_name || field.label.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        label: field.label,
        type: field.type,
        additional_variables: field.additional_variables || [],
        additional_variable_types: field.additional_variable_types || undefined,
        required: field.required || false,
        placeholder: field.placeholder || '',
        description: field.description || '',
        advanced: field.advanced || false,
        defaultValue: field.defaultValue,
        hidden: field.hidden || false,
        min: field.min,
        max: field.max,
        step: field.step,
       rows: field.rows,
        options: field.options || [],
        multiple: field.multiple,
       fileLimit: field.fileLimit,
        accept: field.accept,
        imageOptions: field.imageOptions || [],
        allowCustomValue: field.allowCustomValue || false,
        customValueLabel: field.customValueLabel || '',
        customButtonLabel: field.customButtonLabel || '',
        customInputPlaceholder: field.customInputPlaceholder || '',
        imageValidationPrompt: field.imageValidationPrompt || undefined,
        enablePromptEnhancement: field.enablePromptEnhancement || false,
        systemInstructions: field.systemInstructions || undefined,
        promptBuilderConfig: field.promptBuilderConfig ? {
          template: field.promptBuilderConfig.template || '',
          sub_fields: (field.promptBuilderConfig.sub_fields || []).map((subField: any) => ({
            id: subField.id,
            key: subField.key || subField.variable_name,
            type: subField.type,
            label: subField.label,
            variable_name: subField.variable_name,
            required: subField.required || false,
            placeholder: subField.placeholder || '',
            description: subField.description || '',
            defaultValue: subField.defaultValue,
            options: subField.options || [],
            min: subField.min,
            max: subField.max,
            step: subField.step
          }))
        } : undefined
      }))
    );

    console.log('📋 [MODEL-SAVE] Generated input schema:', inputSchema);

    // Create output mapping based on output key
    const outputMapping = {
      primary_key: modelConfig.outputKey || 'images',
      media_type: getMediaTypeFromOutputKey(modelConfig.outputKey),
      file_extension: getFileExtensionFromOutputKey(modelConfig.outputKey),
      additional_fields: {
        seed: 'seed',
        prompt: 'prompt'
      }
    };

    console.log('🗺️ [MODEL-SAVE] Generated output mapping:', outputMapping);

    // Prepare model data for database
    const modelData = {
      model_id: modelId,
      name: modelConfig.modelName || 'Untitled Model',
      description: modelConfig.description || '',
      category: modelConfig.category || 'models' as const,
      subcategory: modelConfig.subcategory || null,
      icon_name: modelConfig.iconName || 'Zap',
      icon_color: modelConfig.iconColor || 'text-primary',
      icon_bg: modelConfig.iconBg || 'bg-primary/20',
      tags: modelConfig.tags ? modelConfig.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
      fal_endpoint: modelConfig.workflowPath || '',
      credit_cost: modelConfig.creditCost || 10,
      output_type: modelConfig.outputType as 'image' | 'video' | 'audio',
      estimated_time: modelConfig.estimatedTime || null,
      is_available: modelConfig.isAvailable,
      coming_soon: modelConfig.comingSoon,
      featured: modelConfig.featured,
      route: modelConfig.route || null,
      input_schema: inputSchema,
      output_mapping: outputMapping,
      sort_order: modelConfig.sortOrder || 0,
      fal_stream_node_id: modelConfig.fal_stream_node_id || null,
      fal_polling_node_name: modelConfig.fal_polling_node_name || null,
      is_long_running: modelConfig.is_long_running || false,
      polling_interval_seconds: modelConfig.polling_interval_seconds || 60,
      fal_client_mode: modelConfig.fal_client_mode || 'stream',
      fal_queue_webhook_url: modelConfig.fal_queue_webhook_url?.trim() || null,
      hy_user_id: currentUserId || null,
    };

    console.log('💾 [MODEL-SAVE] Prepared model data:', modelData);

    let result;
    if (isUpdate && existingModelId) {
      // Update existing model
      console.log('🔄 [MODEL-SAVE] Updating existing model');
      result = await supabase
        .from('modelconfig')
        .update(modelData)
        .eq('model_id', existingModelId)
        .select('id, model_id')
        .single();
    } else {
      // Insert new model
      console.log('➕ [MODEL-SAVE] Creating new model');
      result = await supabase
        .from('modelconfig')
        .insert(modelData)
        .select('id, model_id')
        .single();
    }

    const { data, error } = result;

    if (error) {
      console.error('❌ [MODEL-SAVE] Database error:', error);

      if (error.code === 'PGRST204' && String(error.message || '').includes('fal_client_mode')) {
        return {
          success: false,
          error:
            'Database is missing modelconfig.fal_client_mode (and possibly fal_queue_webhook_url). ' +
            'Apply Supabase migrations (e.g. npx supabase db push) or run the SQL in ' +
            'supabase/migrations/*modelconfig*fal*.sql in the SQL Editor, then wait a minute for the API schema cache to refresh.',
        };
      }

      // Handle unique constraint violation
      if (error.code === '23505') {
        return {
          success: false,
          error: `Model ID "${modelId}" already exists. Please use a different model name.`
        };
      }

      throw error;
    }

    console.log('✅ [MODEL-SAVE] Model saved successfully:', data);

    return {
      success: true,
      modelId: data.model_id,
      uuid: data.id
    };

  } catch (error) {
    console.error('💥 [MODEL-SAVE] Save error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save model configuration'
    };
  }
};

/**
 * Check if model ID already exists
 */
export const checkModelIdExists = async (modelId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('modelconfig')
      .select('model_id')
      .eq('model_id', modelId)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking model ID:', error);
    return false;
  }
};

/**
 * Load existing model configuration for editing
 */
export const loadModelConfiguration = async (modelId: string): Promise<{
  success: boolean;
  data?: any;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('modelconfig')
      .select('*')
      .eq('model_id', modelId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { success: false, error: 'Model not found' };
      }
      throw error;
    }


    return {
      success: true,
      data: data
    };

  } catch (error) {
    console.error('Error loading model configuration:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to load model configuration'
    };
  }
};

/**
 * Helper function to determine media type from output key
 */
function getMediaTypeFromOutputKey(outputKey: string): 'image' | 'video' | 'audio' {
  const key = outputKey.toLowerCase();
  
  if (key.includes('video')) return 'video';
  if (key.includes('audio') || key.includes('music') || key.includes('sound')) return 'audio';
  return 'image'; // Default to image
}

/**
 * Helper function to determine file extension from output key
 */
function getFileExtensionFromOutputKey(outputKey: string): string {
  const key = outputKey.toLowerCase();
  
  if (key.includes('video')) return 'mp4';
  if (key.includes('audio') || key.includes('music') || key.includes('sound')) return 'mp3';
  return 'jpg'; // Default to jpg for images
}